import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../lib/auth'
import { localDraftStore } from '../lib/draft-storage'
import { draftsApi } from './drafts-api'
import { DraftSync, type CommitEntry, type Values } from './draft-sync'

export type { Values } from './draft-sync'

/**
 * The wizard's in-progress entry, in React terms. The keeping and sending is DraftSync's; this hook
 * holds the values and step as state, opens an existing draft (or takes back one typed offline), and
 * retries when the browser comes back online or the page is left.
 */
export function useDraft({
  orgId,
  farmId,
  kind,
  draftId,
  initialValues,
}: {
  orgId: string
  farmId: string
  kind: string
  draftId: string | null
  initialValues: Values
}) {
  const { user } = useAuth()
  const [values, setValues] = useState<Values>(initialValues)
  const [step, setStepState] = useState(0)
  const [ready, setReady] = useState(draftId === null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // The farm can be chosen on the first step, after the draft object exists.
  const farmRef = useRef(farmId)
  farmRef.current = farmId

  const sync = useMemo(
    () =>
      new DraftSync(
        kind,
        {
          get: (id) => draftsApi.get(orgId, farmRef.current, id),
          create: (body) => draftsApi.create(orgId, farmRef.current, body),
          update: (id, body) => draftsApi.update(orgId, farmRef.current, id, body),
          commit: (id, body) => draftsApi.commit(orgId, farmRef.current, id, body),
        },
        localDraftStore(user?.id ?? 'anonymous'),
        undefined,
        // Starts without an id: an existing draft is opened by `load` below.
        { id: null, step: 0, values: initialValues },
      ),
    // A new draft id from the address (after the first save) is the same entry: keep the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orgId, kind, user?.id],
  )

  const show = useCallback((local: { step: number; values: Values }) => {
    setValues(local.values)
    setStepState(local.step)
    setReady(true)
  }, [])

  // Open an existing draft, or take back an entry of this kind that was typed offline.
  useEffect(() => {
    if (!draftId) {
      const pending = sync.pending()
      if (pending) {
        sync.resumePending(pending)
        show(pending)
      }
      return
    }
    if (sync.id === draftId) return
    let cancelled = false
    sync.load(draftId).then(
      (local) => !cancelled && show(local),
      (err) => !cancelled && setLoadError(err instanceof Error ? err.message : 'Could not open this entry.'),
    )
    return () => {
      cancelled = true
    }
  }, [sync, draftId, show])

  // Retry unsent changes when the connection returns; send what is pending when leaving.
  useEffect(() => {
    const retry = () => void sync.flush()
    window.addEventListener('online', retry)
    window.addEventListener('pagehide', retry)
    return () => {
      window.removeEventListener('online', retry)
      window.removeEventListener('pagehide', retry)
      sync.cancel()
      void sync.flush()
    }
  }, [sync])

  const patch = useCallback(
    (partial: Values) => {
      const next = { ...sync.latest.values, ...partial }
      sync.touch({ values: next })
      setValues(next)
    },
    [sync],
  )
  const setStep = useCallback(
    (n: number) => {
      sync.touch({ step: n })
      setStepState(n)
    },
    [sync],
  )
  const ensureCreated = useCallback(() => sync.ensureCreated(), [sync])
  const commit = useCallback((entries: CommitEntry[]) => sync.commit(entries), [sync])

  return { values, step, ready, loadError, patch, setStep, ensureCreated, commit, draftId: sync.id }
}
